import React, { useContext, useState, useEffect } from "react";
import { ThemeContext } from "../../hooks/ThemeContext";
import { api } from "../../services/baseUrl";
import {
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";

const statusConfig = {
  nouveau: {
    label: "Nouveau",
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/30",
    icon: <Clock size={12} />,
  },
  en_attente: {
    label: "En attente",
    color: "text-yellow-400",
    bg: "bg-yellow-500/10 border-yellow-500/30",
    icon: <Clock size={12} />,
  },
  traite: {
    label: "Traite",
    color: "text-green-400",
    bg: "bg-green-500/10 border-green-500/30",
    icon: <CheckCircle size={12} />,
  },
  lu: {
    label: "Lu",
    color: "text-gray-400",
    bg: "bg-white/5 border-white/10",
    icon: <CheckCircle size={12} />,
  },
};

const Messages = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await api.get("/contact/my-messages");
      setMessages(res.data?.messages || []);
    } catch (err) {
      console.error("Erreur chargement messages:", err);
      toast.error("Erreur lors du chargement des messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleReply = async (msg) => {
    const text = (replyTexts[msg.id] || "").trim();
    if (!text) return;
    try {
      setSendingId(msg.id);
      await api.post(`/contact/messages/${msg.id}/follow-up`, {
        message: text,
      });
      toast.success("Reponse envoyee.");
      setReplyTexts((prev) => ({ ...prev, [msg.id]: "" }));
      await fetchMessages();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Impossible d'envoyer la reponse.",
      );
    } finally {
      setSendingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div
        className={`text-center py-10 ${isDark ? "text-white/50" : "text-gray-400"}`}
      >
        Chargement...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.length === 0 ? (
        <div
          className={`text-center py-12 rounded-xl ${isDark ? "bg-[#0b0b0c] border border-white/6" : "bg-white border border-gray-100"}`}
        >
          <MessageSquare className="mx-auto mb-3 opacity-30" size={40} />
          <p
            className={`font-semibold mb-1 ${isDark ? "text-white/60" : "text-gray-600"}`}
          >
            Aucun message
          </p>
          <p
            className={`text-sm ${isDark ? "text-white/40" : "text-gray-400"}`}
          >
            Vous n'avez aucun echange pour le moment.
          </p>
        </div>
      ) : (
        messages.map((msg) => {
          const st = statusConfig[msg.status] || statusConfig.nouveau;
          const isOpen = expanded === msg.id;
          const hasReply = !!msg.response;

          return (
            <div
              key={msg.id}
              className={`rounded-xl border overflow-hidden transition-all ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
            >
              <button
                className="w-full text-left px-4 py-3 flex items-center justify-between gap-3"
                onClick={() => setExpanded(isOpen ? null : msg.id)}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border shrink-0 ${st.bg} ${st.color}`}
                  >
                    {st.icon} {st.label}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {msg.service || "Message general"}
                    </p>
                    <p
                      className={`text-xs truncate ${isDark ? "text-white/40" : "text-gray-400"}`}
                    >
                      {msg.message?.slice(0, 80)}
                      {msg.message?.length > 80 ? "..." : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {hasReply && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/20">
                      Reponse recue
                    </span>
                  )}
                  <span
                    className={`text-xs ${isDark ? "text-white/30" : "text-gray-400"}`}
                  >
                    {formatDate(msg.created_at)}
                  </span>
                  {isOpen ? (
                    <ChevronUp size={16} className="opacity-40" />
                  ) : (
                    <ChevronDown size={16} className="opacity-40" />
                  )}
                </div>
              </button>

              {isOpen && (
                <div
                  className={`px-4 pb-4 space-y-3 border-t ${isDark ? "border-white/6" : "border-gray-100"}`}
                >
                  {/* Message initial de l'utilisateur */}
                  <div
                    className={`mt-3 p-3 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                  >
                    <p
                      className={`text-xs font-semibold mb-1 ${isDark ? "text-white/40" : "text-gray-400"}`}
                    >
                      Votre message
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    <p
                      className={`text-xs mt-2 ${isDark ? "text-white/30" : "text-gray-400"}`}
                    >
                      {formatDate(msg.created_at)}
                    </p>
                  </div>

                  {/* Fil de discussion (replies) */}
                  {msg.replies &&
                    msg.replies.length > 0 &&
                    msg.replies.map((r) => {
                      const isAdmin = !r.id_user || r.name === "Krayne Agency";
                      return (
                        <div
                          key={r.id}
                          className={`p-3 rounded-lg border ${
                            isAdmin
                              ? isDark
                                ? "bg-green-500/5 border-green-500/20"
                                : "bg-green-50 border-green-200"
                              : isDark
                                ? "bg-white/5 border-white/10"
                                : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <p
                            className={`text-xs font-semibold mb-1 ${isAdmin ? (isDark ? "text-green-400" : "text-green-700") : isDark ? "text-white/50" : "text-gray-500"}`}
                          >
                            {isAdmin ? "Reponse de Krayne Agency" : "Vous"}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">
                            {r.message}
                          </p>
                          <p
                            className={`text-xs mt-2 ${isDark ? "text-white/30" : "text-gray-400"}`}
                          >
                            {formatDate(r.created_at)}
                          </p>
                        </div>
                      );
                    })}

                  {/* Pas encore de réponse */}
                  {(!msg.replies || msg.replies.length === 0) && (
                    <div
                      className={`flex items-center gap-2 p-3 rounded-lg text-sm ${isDark ? "bg-yellow-500/5 border border-yellow-500/20 text-yellow-400" : "bg-yellow-50 border border-yellow-200 text-yellow-700"}`}
                    >
                      <AlertCircle size={14} />
                      Votre message est en cours de traitement. Vous recevrez
                      une reponse prochainement.
                    </div>
                  )}

                  {/* Zone de réponse (toujours disponible) */}
                  <div
                    className={`p-3 rounded-lg border space-y-2 ${isDark ? "bg-white/3 border-white/8" : "bg-gray-50 border-gray-200"}`}
                  >
                    <p
                      className={`text-xs font-semibold ${isDark ? "text-white/50" : "text-gray-500"}`}
                    >
                      Repondre
                    </p>
                    <textarea
                      value={replyTexts[msg.id] || ""}
                      onChange={(e) =>
                        setReplyTexts((prev) => ({
                          ...prev,
                          [msg.id]: e.target.value,
                        }))
                      }
                      placeholder="Votre reponse..."
                      rows={3}
                      className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${isDark ? "bg-white/5 border-white/10 text-white placeholder:text-gray-600" : "bg-white border-gray-200 text-gray-900"}`}
                    />
                    <button
                      onClick={() => handleReply(msg)}
                      disabled={
                        sendingId === msg.id ||
                        !(replyTexts[msg.id] || "").trim()
                      }
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                      style={{
                        background:
                          "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
                      }}
                    >
                      <Send size={14} />
                      {sendingId === msg.id ? "Envoi..." : "Envoyer"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default Messages;
